import React, { useEffect, useRef, useState } from 'react'
import Nav from 'react-bootstrap/Nav'
import Navbar from 'react-bootstrap/Navbar'
import { graphql, useStaticQuery } from 'gatsby'
import DOMPurify from 'dompurify'

import * as style from './header.module.scss'

import { resolvePctLinkUrl } from '@/internal/utils/resolve-pctlink-url'

const Header = () => {
  const data = useStaticQuery(graphql`
    {
      reporterHeaderNavigation: allDatoCmsPctNavigation(
        filter: { codeId: { eq: "insurance-report-header-navigation" } }
      ) {
        nodes {
          navigationItems {
            ...PctNavigationItems
          }
        }
      }
    }
  `)

  const mainNavigationItems =
    data.reporterHeaderNavigation?.nodes[0]?.navigationItems.find(
      (item) => item.codeId === 'report-header-main'
    )?.navigationLinks ?? []

  const topNavigationItems =
    data.reporterHeaderNavigation?.nodes[0]?.navigationItems.find(
      (item) => item.codeId === 'report-header-social'
    )?.navigationLinks ?? []

  const collapseNavigationItems =
    data.reporterHeaderNavigation?.nodes[0]?.navigationItems.find(
      (item) => item.codeId === 'report-header-menu'
    )?.navigationLinks ?? []

  const collapseLeftNavigationItems =
    data.reporterHeaderNavigation?.nodes[0]?.navigationItems.find(
      (item) => item.codeId === 'report-menu-footer-left'
    )?.navigationLinks ?? []

  const collapseRightNavigationItems =
    data.reporterHeaderNavigation?.nodes[0]?.navigationItems.find(
      (item) => item.codeId === 'report-menu-footer-right'
    )?.navigationLinks ?? []

  const breadcrumbLink =
    data.reporterHeaderNavigation?.nodes[0]?.navigationItems.find(
      (item) => item.codeId === 'report-header-breadcrumb'
    )?.mainLink ?? null

  const menu = useRef(null)
  const [shareUrl, setShareUrl] = useState('')

  useEffect(() => {
    typeof window !== 'undefined'
      ? setShareUrl(
          encodeURIComponent(DOMPurify.sanitize(window.location?.href))
        )
      : setShareUrl('')
  }, [])

  const [menuMainShow, setMenuMainShow] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleScroll = () => {
      if (window.pageYOffset > 10) {
        menu.current.classList.add(style.active)
      } else {
        menu.current.classList.remove(style.active)
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const handleMainMenu = () => {
    setMenuMainShow(!menuMainShow)
    menu.current.classList.add(style.active)
    if (menuMainShow) {
      document.querySelector('body').classList.remove('scroll-menu')
    } else {
      document.querySelector('body').classList.add('scroll-menu')
    }
  }

  const resolveSocial = ({ codeId, url = '' }) => {
    switch (codeId) {
      case 'facebook':
        return 'https://www.facebook.com/sharer/sharer.php?u=' + shareUrl
      case 'twitter':
        return 'https://twitter.com/intent/tweet?url=' + shareUrl
      case 'linkedin':
        return 'https://www.linkedin.com/shareArticle?mini=true&url=' + shareUrl
      case 'download-report':
        return url
      default:
        return url
    }
  }

  const handleShare = async (e) => {
    try {
      e.preventDefault()

      if (
        typeof window !== 'undefined' &&
        typeof navigator?.share === 'function'
      ) {
        navigator.share({
          title: document.title,
          url: window.location?.href
        })
        return
      }

      if (typeof window !== 'undefined' && navigator?.clipboard) {
        await navigator.clipboard.writeText(window.location?.href)
        const existsTooltip = document.getElementById('tooltip')

        if (!existsTooltip) {
          const span = document.createElement('span')
          span.innerText = 'Link copied to clipboard!'
          span.className = style.tooltip
          span.id = 'tooltip'

          e.target.parentElement.parentElement.appendChild(span)

          setTimeout(() => {
            span.remove()
          }, 2500)
        }
      }
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <header className={style.header} ref={menu}>
      <div className={style.topMenu}>
        <div className="container">
          <div className="row align-items-center">
            <div
              className={`${
                menuMainShow ? 'd-none d-lg-block' : ''
              } col-8 col-lg-4`}
            >
              <div className={style.colBranding}>
                <div className={style.logo}>
                  <Navbar.Brand href="/">
                    <img
                      src="/media/header_logo.svg"
                      alt="logo ntt data"
                      className={style.logo}
                      width="296"
                      height="40"
                    />
                  </Navbar.Brand>
                </div>
                <div className={style.title}>Insurance</div>
              </div>
            </div>
            <div
              className={`${
                menuMainShow ? 'col-12' : 'col-4'
              } col-lg-8 d-flex align-items-center justify-content-between justify-content-lg-end ${
                style.mainNav
              }`}
            >
              <Nav
                activeKey={data.pathname}
                className={`d-flex justify-content-between justify-content-lg-end align-items-lg-center`}
              >
                <span
                  className={`b100 text-base-1000 d-none d-lg-block ${style.textShare}`}
                >
                  SHARE THIS
                </span>
                <div className={`d-flex align-items-center ${style.navSocial}`}>
                  {topNavigationItems.map((item, index) => {
                    const isShareItem = item.codeId === 'share'
                    const isButton = topNavigationItems.length - 1 === index

                    return (
                      <div
                        className={[
                          'position-relative',
                          menuMainShow && (isShareItem || isButton)
                            ? 'd-inline-flex'
                            : 'd-none d-lg-inline-block'
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        <Nav.Link
                          onClick={isShareItem ? handleShare : null}
                          key={`link-social-${index}`}
                          href={resolveSocial(item)}
                          className={[
                            style.itemSocial,
                            isButton ? style.button : '',
                            isShareItem ? style.itemSocialShare : ''
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          <span
                            aria-hidden
                            className={[
                              !isShareItem && !isButton && 'd-none',
                              isShareItem && 'd-inline-flex d-lg-none',
                              isButton && 'd-inline-flex'
                            ]
                              .filter(Boolean)
                              .join(' ')}
                          >
                            {item.externalName}
                          </span>
                          {item.icon.url && (
                            <img src={item.icon.url} alt={item.externalName} />
                          )}
                        </Nav.Link>
                      </div>
                    )
                  })}
                </div>
              </Nav>
              <button
                aria-controls="basic-navbar-nav"
                className={`${style.cross} navbar-toggler ${
                  menuMainShow ? '' : style.collapsed
                }`}
                onClick={handleMainMenu}
              >
                <span />
                <span />
                <span />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        id="basic-navbar-nav"
        className={`${style.menuCollapse} navbar-collapse ${
          menuMainShow ? `${style.show}` : ''
        }`}
      >
        <div className="container">
          <div className="row">
            <div className={`${style.breadcrumb} col-12 col-lg-5 `}>
              {breadcrumbLink && (
                <Nav.Link
                  href={resolvePctLinkUrl(breadcrumbLink)}
                  className={`${style.breadcrumbItem} m-0 p-0 b100`}
                >
                  {breadcrumbLink?.icon?.url && (
                    <img
                      src={breadcrumbLink.icon.url}
                      alt={breadcrumbLink.externalName}
                    />
                  )}
                  {breadcrumbLink?.externalName}
                </Nav.Link>
              )}
            </div>

            <div className={`${style.mainMenu} col-12 col-lg-7`}>
              <Nav
                activeKey={data.pathname}
                className={`${style.menuWeb} justify-content-lg-end align-items-lg-center`}
              >
                {mainNavigationItems.map((item, index) => (
                  <div
                    key={`link-main-${index}`}
                    className="d-flex align-items-center"
                  >
                    <Nav.Link
                      href={resolvePctLinkUrl(item)}
                      className={`${style.item} m-0 p-0 b100 `}
                    >
                      {item.externalName}
                    </Nav.Link>
                    {mainNavigationItems.length - 1 !== index && (
                      <span className={style.spacer}>|</span>
                    )}
                  </div>
                ))}
              </Nav>
            </div>
          </div>
        </div>

        <div className="container-fluid p-0">
          <div className="row m-0">
            <div className={`col-12 p-0`}>
              <Nav activeKey={data.pathname} className={style.navSlide}>
                {collapseNavigationItems.map((item, index) => (
                  <Nav.Link
                    onClick={handleMainMenu}
                    key={`link-slide-${index}`}
                    href={resolvePctLinkUrl(item)}
                    data-index={index + 1}
                    className={`${style.itemSlide} m-0`}
                  >
                    <p
                      className={`container m-auto d-flex h200 ${
                        index !== 0 ? 'text-uppercase' : ''
                      }`}
                    >
                      <span>{item.externalName}</span>
                    </p>
                  </Nav.Link>
                ))}
              </Nav>
            </div>
          </div>
        </div>

        <div className="container">
          <div className="row">
            {collapseLeftNavigationItems.length > 0 && (
              <div className="col-6 col-lg-3">
                <Nav activeKey={data.pathname} className={style.menuBottom}>
                  {collapseLeftNavigationItems.map((item, index) => (
                    <Nav.Link
                      key={`link-left-${index}`}
                      href={resolvePctLinkUrl(item)}
                      className={`m-0 p-0 text-uppercase ${style.itemBottom}`}
                    >
                      {item.externalName}
                    </Nav.Link>
                  ))}
                </Nav>
              </div>
            )}

            <div className="col-6 col-lg-3">
              <Nav activeKey={data.pathname} className={style.menuBottomRight}>
                {collapseRightNavigationItems.map((item, index) => (
                  <Nav.Link
                    key={`link-right-${index}`}
                    href={resolvePctLinkUrl(item)}
                    className={`m-0 p-0 ${style.itemBottomRight}`}
                  >
                    {item.externalName}
                  </Nav.Link>
                ))}
              </Nav>
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}

export default Header
